import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, streamPost, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { buildEditSrc } from "@/lib/editorRuntime";
import Inspector from "@/components/Inspector";
import SectionsPanel from "@/components/SectionsPanel";
import TemplateGallery from "@/components/TemplateGallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Loader2, Rocket, Send, Sparkles, MessageSquare, ListChecks, LayoutTemplate,
  Monitor, Tablet, Smartphone, MessageCircle, ExternalLink, Copy, Check,
  Eye, MousePointerClick, Layers, Plus, MoreVertical, Pencil, Trash2, Undo2, Redo2, Move,
  LogOut, MonitorSmartphone, AlertTriangle, Clock, Coins,
} from "lucide-react";

const clientSlugify = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const WIZARD_QUESTIONS = [
  { key: "tipo", q: "Che tipo di sito vuoi creare?", ph: "Es. sito per il mio ristorante, portfolio, landing page di un'app..." },
  { key: "nome", q: "Qual è il nome dell'attività o del progetto?", ph: "Es. Trattoria da Luigi" },
  { key: "descrizione", q: "Descrivi in poche parole cosa offri.", ph: "Es. cucina tradizionale romana, ambiente familiare" },
  { key: "stile", q: "Che stile e colori preferisci?", ph: "Es. elegante, toni caldi, rustico moderno" },
  { key: "sezioni", q: "Quali sezioni ti servono?", ph: "Es. menù, galleria foto, orari, contatti e mappa" },
];

function buildWizardPrompt(a) {
  return (
    `Crea un sito web completo e bello con queste informazioni:\n` +
    `- Tipo di sito: ${a.tipo || "non specificato"}\n- Nome: ${a.nome || "non specificato"}\n` +
    `- Cosa offre: ${a.descrizione || "non specificato"}\n- Stile e colori: ${a.stile || "a tua scelta, moderno"}\n` +
    `- Sezioni richieste: ${a.sezioni || "le sezioni più adatte"}\nUsa contenuti realistici e in italiano. Rendilo responsive e moderno.`
  );
}

const DEVICES = {
  desktop: { w: "100%", icon: Monitor, label: "Desktop" },
  tablet: { w: "820px", icon: Tablet, label: "Tablet" },
  mobile: { w: "390px", icon: Smartphone, label: "Mobile" },
};

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout, logoutAll } = useAuth();
  const [project, setProject] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [phase, setPhase] = useState("loading");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState("");
  const [input, setInput] = useState("");
  const [title, setTitle] = useState("");
  const [wizardStep, setWizardStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [wizardInput, setWizardInput] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [mobileTab, setMobileTab] = useState("chat");
  const [editMode, setEditMode] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [selected, setSelected] = useState(null);
  const [showSections, setShowSections] = useState(false);
  const [sections, setSections] = useState([]);
  const [moveMode, setMoveMode] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [pageDialog, setPageDialog] = useState(null); // {mode:'add'|'rename', slug?, label}
  const [, setHistVersion] = useState(0);
  const [usage, setUsage] = useState(null);
  const [resetLeft, setResetLeft] = useState(0);
  const [publishDialog, setPublishDialog] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [slugStatus, setSlugStatus] = useState(null); // {checking, available, valid}
  const [showcaseOn, setShowcaseOn] = useState(true);

  const chatEndRef = useRef(null);
  const iframeRef = useRef(null);
  const htmlResolveRef = useRef(null);
  const historiesRef = useRef({});
  const moveModeRef = useRef(false);

  const curPage = pages.find((p) => p.slug === activePage);
  const html = curPage?.html || "";

  // history helpers
  const initHist = (slug, h) => { if (!historiesRef.current[slug]) historiesRef.current[slug] = { stack: [h || ""], idx: 0 }; };
  const commitHist = (slug, h) => {
    const cur = historiesRef.current[slug] || { stack: [], idx: -1 };
    if (cur.stack[cur.idx] === h) return;
    const stack = cur.stack.slice(0, cur.idx + 1);
    stack.push(h);
    historiesRef.current[slug] = { stack, idx: stack.length - 1 };
    setHistVersion((v) => v + 1);
  };
  const h = historiesRef.current[activePage];
  const canUndo = !!h && h.idx > 0;
  const canRedo = !!h && h.idx < h.stack.length - 1;

  useEffect(() => {
    api.get(`/projects/${id}`).then(({ data }) => {
      setProject(data);
      setTitle(data.title);
      setMessages(data.messages || []);
      const pg = data.pages || [];
      setPages(pg);
      pg.forEach((p) => initHist(p.slug, p.html));
      if (data.published && data.slug) setPublishedUrl(`${process.env.REACT_APP_BACKEND_URL}/api/s/${data.slug}`);
      const home = pg.find((p) => p.slug === "home");
      setPhase(home?.html ? "ready" : "onboarding");
    }).catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); navigate("/dashboard"); });
    api.get("/usage").then(({ data }) => { setUsage(data); setResetLeft(data.reset_seconds || 0); }).catch(() => {});
    // load the project once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const refreshUsage = () => api.get("/usage").then(({ data }) => { setUsage(data); setResetLeft(data.reset_seconds || 0); }).catch(() => {});

  // countdown ticker when AI generations are exhausted
  useEffect(() => {
    if (resetLeft <= 0) return;
    const t = setInterval(() => {
      setResetLeft((s) => {
        if (s <= 1) { refreshUsage(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetLeft > 0]);

  // live slug availability check (debounced) while the publish dialog is open
  useEffect(() => {
    if (!publishDialog) return;
    const clean = clientSlugify(slugInput);
    if (!clean) { setSlugStatus({ valid: false, available: false }); return; }
    setSlugStatus({ checking: true });
    const t = setTimeout(() => {
      api.get(`/projects/${id}/slug-check`, { params: { slug: slugInput } })
        .then(({ data }) => setSlugStatus(data))
        .catch(() => setSlugStatus({ valid: true, available: true }));
    }, 450);
    return () => clearTimeout(t);
  }, [slugInput, publishDialog, id]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  useEffect(() => {
    const handler = (e) => {
      const d = e.data || {};
      if (!d.__editor) return;
      if (d.type === "selected") setSelected(d.payload);
      else if (d.type === "ready") { setSelected(null); postToIframe("getsections"); postToIframe("setmove", { enabled: moveModeRef.current }); }
      else if (d.type === "sections") setSections(d.list || []);
      else if (d.type === "html") { if (htmlResolveRef.current) { htmlResolveRef.current(d.html); htmlResolveRef.current = null; } }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // handler uses stable refs (iframeRef, moveModeRef); attach once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const postToIframe = (cmd, extra = {}) => {
    const w = iframeRef.current?.contentWindow;
    if (w) w.postMessage({ __cmd: true, cmd, ...extra }, "*");
  };
  const sendCmd = (cmd, extra = {}) => { if (selected) postToIframe(cmd, { id: selected.id, ...extra }); };

  const setPageHtmlLocal = (slug, newHtml) => setPages((ps) => ps.map((p) => (p.slug === slug ? { ...p, html: newHtml } : p)));

  const pullHtml = () => new Promise((resolve) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return resolve(null);
    htmlResolveRef.current = resolve;
    w.postMessage({ __cmd: true, cmd: "gethtml" }, "*");
    setTimeout(() => { if (htmlResolveRef.current) { htmlResolveRef.current(null); htmlResolveRef.current = null; resolve(null); } }, 2500);
  });

  const persistPage = async (slug, newHtml) => {
    try { await api.patch(`/projects/${id}`, { html: newHtml, page: slug }); }
    catch (e) { toast.error("Impossibile salvare: " + formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveEdits = async () => {
    if (!editMode) return html;
    const newHtml = await pullHtml();
    if (newHtml) {
      setPageHtmlLocal(activePage, newHtml);
      commitHist(activePage, newHtml);
      await persistPage(activePage, newHtml);
      return newHtml;
    }
    return html;
  };

  const switchMode = async (toEdit) => {
    if (toEdit) { setSelected(null); setEditMode(true); toast.info("Modalità modifica: clicca un elemento per cambiarlo."); }
    else { await saveEdits(); setSelected(null); setShowSections(false); setMoveMode(false); moveModeRef.current = false; setEditMode(false); toast.success("Modifiche salvate"); }
  };

  const toggleMove = () => {
    const next = !moveMode;
    setMoveMode(next); moveModeRef.current = next;
    postToIframe("setmove", { enabled: next });
    toast.info(next ? "Sposta: trascina l'elemento selezionato; usa l'angolo per ridimensionare." : "Modalità sposta disattivata");
  };

  const switchPage = async (slug) => {
    if (slug === activePage) return;
    if (editMode) await saveEdits();
    setEditMode(false); setShowSections(false); setMoveMode(false); moveModeRef.current = false; setSelected(null);
    setActivePage(slug);
    const p = pages.find((x) => x.slug === slug);
    initHist(slug, p?.html || "");
    setPhase(p?.html ? "ready" : "onboarding");
  };

  const applyHistory = async (dir) => {
    const hist = historiesRef.current[activePage];
    if (!hist) return;
    const nextIdx = dir === "undo" ? hist.idx - 1 : hist.idx + 1;
    if (nextIdx < 0 || nextIdx >= hist.stack.length) return;
    hist.idx = nextIdx; setHistVersion((v) => v + 1);
    const target = hist.stack[nextIdx];
    setSelected(null);
    setPageHtmlLocal(activePage, target);
    await persistPage(activePage, target);
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Immagine caricata");
      return data.url;
    } catch (e) { toast.error("Caricamento non riuscito: " + formatApiErrorDetail(e.response?.data?.detail)); return null; }
  };

  const runGenerate = async (prompt, isEdit) => {
    if (editMode) { await saveEdits(); setEditMode(false); setSelected(null); setShowSections(false); setMoveMode(false); moveModeRef.current = false; }
    setStreaming(true); setStreamBuffer(""); setMobileTab("preview");
    setMessages((m) => [...m, { role: "user", content: prompt, at: new Date().toISOString() }]);
    let acc = "";
    await streamPost(
      `/projects/${id}/${isEdit ? "edit" : "generate"}`,
      isEdit ? { instruction: prompt, page: activePage } : { prompt, page: activePage },
      {
        onDelta: (d) => { acc += d; setStreamBuffer(acc); },
        onDone: (finalHtml) => {
          setPageHtmlLocal(activePage, finalHtml);
          commitHist(activePage, finalHtml);
          setStreaming(false); setStreamBuffer(""); setPhase("ready");
          setMessages((m) => [...m, { role: "assistant", content: isEdit ? "Ho aggiornato la pagina come richiesto." : "Ho creato la prima versione della pagina.", at: new Date().toISOString() }]);
          refreshUsage();
        },
        onError: (msg) => { setStreaming(false); setStreamBuffer(""); toast.error("Errore AI: " + msg); setMessages((m) => [...m, { role: "assistant", content: "Si è verificato un errore. Riprova.", at: new Date().toISOString() }]); refreshUsage(); },
      }
    );
  };

  const pickTemplate = async (t) => {
    setShowGallery(false);
    setPageHtmlLocal(activePage, t.html);
    commitHist(activePage, t.html);
    setPhase("ready");
    await persistPage(activePage, t.html);
    toast.success("Modello applicato! Ora personalizzalo.");
  };

  const startPrompt = () => setPhase("ready");
  const startWizard = () => { setPhase("wizard"); setWizardStep(0); setAnswers({}); setWizardInput(""); };
  const nextWizard = () => {
    const key = WIZARD_QUESTIONS[wizardStep].key;
    const updated = { ...answers, [key]: wizardInput.trim() };
    setAnswers(updated); setWizardInput("");
    if (wizardStep < WIZARD_QUESTIONS.length - 1) setWizardStep((s) => s + 1);
    else { setPhase("ready"); runGenerate(buildWizardPrompt(updated), false); }
  };

  const sendMessage = () => { const t = input.trim(); if (!t || streaming) return; setInput(""); runGenerate(t, !!html); };

  const saveTitle = async () => {
    if (!title.trim() || title === project.title) return;
    try { await api.patch(`/projects/${id}`, { title: title.trim() }); setProject((p) => ({ ...p, title: title.trim() })); } catch (e) { console.error("saveTitle failed", e); }
  };

  const openPublish = () => {
    setSlugInput(project?.slug || clientSlugify(title) || "");
    setSlugStatus(null);
    setShowcaseOn(!project?.showcase_hidden);
    setPublishDialog(true);
  };

  const doPublish = async () => {
    const clean = clientSlugify(slugInput);
    if (!clean) { toast.error("Scegli un indirizzo valido per il sito"); return; }
    setPublishing(true);
    try {
      if (editMode) await saveEdits();
      const body = { slug: clean };
      if (user?.plan === "pro") body.showcase_hidden = !showcaseOn;
      const { data } = await api.post(`/projects/${id}/publish`, body);
      setPublishedUrl(`${process.env.REACT_APP_BACKEND_URL}/api/s/${data.slug}`);
      setProject((p) => ({ ...p, published: true, slug: data.slug, showcase_hidden: !showcaseOn }));
      setPublishDialog(false);
      toast.success("Sito pubblicato con successo!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const copyUrl = () => { navigator.clipboard.writeText(publishedUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const reorderSections = (newList) => { setSections(newList); postToIframe("reorder", { order: newList.map((s) => s.id) }); };
  const deleteSection = (sid) => postToIframe("delete", { id: sid });
  const insertSection = (t) => postToIframe("insertSection", { html: t.html });

  // page management
  const submitPageDialog = async () => {
    const label = (pageDialog.label || "").trim();
    if (!label) return;
    try {
      if (pageDialog.mode === "add") {
        const { data } = await api.post(`/projects/${id}/pages`, { label });
        setPages(data.pages);
        data.pages.forEach((p) => initHist(p.slug, p.html));
        setPageDialog(null);
        switchPage(data.slug);
      } else {
        const { data } = await api.patch(`/projects/${id}/pages/${pageDialog.slug}`, { label });
        setPages(data.pages); setPageDialog(null);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removePage = async (slug) => {
    try {
      const { data } = await api.delete(`/projects/${id}/pages/${slug}`);
      delete historiesRef.current[slug];
      setPages(data.pages);
      if (activePage === slug) switchPage("home");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (phase === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const iframeSrc = streaming ? streamBuffer : editMode ? buildEditSrc(html) : html;
  const canEdit = !!html && !streaming;

  return (
    <div className="h-screen flex flex-col bg-background text-white overflow-hidden">
      <header className="h-14 shrink-0 border-b border-white/10 backdrop-blur-xl bg-black/50 flex items-center justify-between px-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-white p-1" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle}
            className="h-8 w-32 sm:w-48 bg-transparent border-transparent hover:border-white/10 focus:border-white/20 font-display font-semibold truncate px-2" data-testid="project-title-input" />
        </div>
        <div className="flex items-center gap-2">
          {publishedUrl && (
            <a href={publishedUrl} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1 text-xs text-success hover:underline" data-testid="view-live-link"><ExternalLink className="w-3.5 h-3.5" /> Online</a>
          )}
          <Button onClick={openPublish} disabled={publishing || !(pages.find((p) => p.slug === "home")?.html) || streaming}
            className="bg-primary text-black hover:bg-[#33F3FF] font-semibold h-9 px-4 transition-transform hover:-translate-y-0.5 disabled:opacity-40" data-testid="publish-btn">
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1.5" /> Pubblica</>}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="shrink-0" data-testid="builder-user-menu-trigger">
                <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold text-sm hover:bg-white/20 transition-colors">
                  {(user?.name || user?.email || "U")[0].toUpperCase()}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#141414] border-white/10 text-white">
              <div className="px-2 py-1.5 text-xs text-muted-foreground truncate max-w-[200px]">{user?.email}</div>
              <DropdownMenuItem onClick={logout} className="cursor-pointer" data-testid="builder-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logoutAll} className="cursor-pointer" data-testid="builder-logout-all-btn">
                <MonitorSmartphone className="w-4 h-4 mr-2" /> Esci da tutti i dispositivi
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Page tabs */}
      <div className="h-11 shrink-0 border-b border-white/10 flex items-center px-2 gap-1 overflow-x-auto bg-black/20">
        {pages.map((p) => (
          <div key={p.slug} className={`shrink-0 flex items-center rounded-lg ${activePage === p.slug ? "bg-white/10" : ""}`}>
            <button onClick={() => switchPage(p.slug)}
              className={`flex items-center gap-1.5 text-sm pl-3 pr-2 py-1.5 rounded-lg transition-colors ${activePage === p.slug ? "text-white font-semibold" : "text-muted-foreground hover:text-white"}`}
              data-testid={`page-tab-${p.slug}`}>
              {p.label}
              <span className={`w-1.5 h-1.5 rounded-full ${p.html ? "bg-success" : "bg-white/20"}`} />
            </button>
            {!p.fixed && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-muted-foreground hover:text-white pr-2 py-1.5" data-testid={`page-menu-${p.slug}`}><MoreVertical className="w-3.5 h-3.5" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#141414] border-white/10 text-white">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => setPageDialog({ mode: "rename", slug: p.slug, label: p.label })} data-testid={`page-rename-${p.slug}`}><Pencil className="w-4 h-4 mr-2" /> Rinomina</DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer text-destructive focus:text-destructive" onClick={() => removePage(p.slug)} data-testid={`page-delete-${p.slug}`}><Trash2 className="w-4 h-4 mr-2" /> Elimina</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ))}
        <button onClick={() => setPageDialog({ mode: "add", label: "" })} className="shrink-0 flex items-center gap-1 text-sm px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-white hover:bg-white/5" data-testid="add-page-btn">
          <Plus className="w-4 h-4" /> Pagina
        </button>
      </div>

      {/* Mobile tabs */}
      <div className="md:hidden flex border-b border-white/10 shrink-0">
        <button onClick={() => setMobileTab("chat")} className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5 ${mobileTab === "chat" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`} data-testid="mobile-tab-chat"><MessageCircle className="w-4 h-4" /> Chat</button>
        <button onClick={() => setMobileTab("preview")} className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5 ${mobileTab === "preview" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`} data-testid="mobile-tab-preview"><Monitor className="w-4 h-4" /> Anteprima</button>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Left panel */}
        <div className={`w-full md:w-[400px] md:shrink-0 border-r border-white/10 flex flex-col min-h-0 ${mobileTab === "chat" ? "flex" : "hidden md:flex"}`} data-testid="left-panel">
          {phase === "onboarding" && (
            <div className="flex-1 flex flex-col justify-center p-6 gap-3 overflow-y-auto">
              <div className="text-center mb-1">
                <div className="w-12 h-12 mx-auto rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4"><Sparkles className="w-6 h-6" /></div>
                <h2 className="font-display font-bold text-xl">Crea la pagina "{curPage?.label}"</h2>
                <p className="text-sm text-muted-foreground mt-1">Scegli come iniziare.</p>
              </div>
              <button onClick={() => setShowGallery(true)} className="text-left border border-white/10 rounded-xl p-4 hover:border-primary/40 hover:bg-white/[0.03] transition-all" data-testid="mode-template-btn">
                <div className="flex items-center gap-2 font-semibold"><LayoutTemplate className="w-4 h-4 text-primary" /> Parti da un modello</div>
                <p className="text-sm text-muted-foreground mt-1">Scegli un sito già pronto e personalizzalo.</p>
              </button>
              <button onClick={startPrompt} className="text-left border border-white/10 rounded-xl p-4 hover:border-primary/40 hover:bg-white/[0.03] transition-all" data-testid="mode-prompt-btn">
                <div className="flex items-center gap-2 font-semibold"><MessageSquare className="w-4 h-4 text-primary" /> Descrivi a parole</div>
                <p className="text-sm text-muted-foreground mt-1">Scrivi una frase e l'AI costruisce la pagina.</p>
              </button>
              <button onClick={startWizard} className="text-left border border-white/10 rounded-xl p-4 hover:border-primary/40 hover:bg-white/[0.03] transition-all" data-testid="mode-wizard-btn">
                <div className="flex items-center gap-2 font-semibold"><ListChecks className="w-4 h-4 text-primary" /> Rispondi a 5 domande</div>
                <p className="text-sm text-muted-foreground mt-1">Ti guidiamo passo passo.</p>
              </button>
            </div>
          )}

          {phase === "wizard" && (
            <div className="flex-1 flex flex-col p-6">
              <div className="text-xs font-mono text-muted-foreground mb-2">Domanda {wizardStep + 1} di {WIZARD_QUESTIONS.length}</div>
              <div className="w-full h-1 bg-white/10 rounded-full mb-6 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${((wizardStep + 1) / WIZARD_QUESTIONS.length) * 100}%` }} /></div>
              <AnimatePresence mode="wait">
                <motion.div key={wizardStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="flex-1 flex flex-col">
                  <h2 className="font-display font-bold text-xl mb-4">{WIZARD_QUESTIONS[wizardStep].q}</h2>
                  <Textarea autoFocus value={wizardInput} onChange={(e) => setWizardInput(e.target.value)} placeholder={WIZARD_QUESTIONS[wizardStep].ph} className="bg-white/5 border-white/10 min-h-28 resize-none" data-testid="wizard-input" />
                  <div className="mt-auto pt-4"><Button onClick={nextWizard} className="w-full bg-primary text-black hover:bg-[#33F3FF] font-semibold h-11" data-testid="wizard-next-btn">{wizardStep < WIZARD_QUESTIONS.length - 1 ? "Continua" : "Crea la pagina"}</Button></div>
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {phase === "ready" && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && !streaming && (
                  <div className="text-center text-sm text-muted-foreground py-10 px-4"><MessageSquare className="w-8 h-8 mx-auto mb-3 text-white/20" />Descrivi la pagina che vuoi creare, oppure chiedi modifiche a parole.</div>
                )}
                {messages.map((m, i) => (
                  <div key={`${m.at || "m"}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                    <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-white/[0.07] text-white" : "bg-primary/10 border border-primary/20 text-white"}`}>{m.content}</div>
                  </div>
                ))}
                {streaming && (<div className="flex justify-start"><div className="max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm bg-primary/10 border border-primary/20 pulse-border flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin text-primary" /> L'AI sta scrivendo...</div></div>)}
                <div ref={chatEndRef} />
              </div>
              <div className="border-t border-white/10 p-3">
                {usage && (usage.ai_remaining > 0 ? (
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-2 px-1 flex-wrap" data-testid="ai-usage-badge">
                    <Sparkles className="w-3 h-3 text-primary" />
                    <span className={usage.ai_remaining <= 5 ? "text-destructive" : ""}>
                      {usage.ai_remaining}/{usage.ai_limit} generazioni AI rimaste
                    </span>
                    <span className="opacity-60">· si ricaricano ogni {usage.window_minutes} min</span>
                    {usage.ai_credits > 0 && (
                      <span className="text-primary">· +{usage.ai_credits} crediti extra</span>
                    )}
                  </div>
                ) : usage.ai_credits > 0 ? (
                  <div className="mb-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2.5 text-xs" data-testid="ai-credits-notice">
                    <div className="flex items-center gap-1.5 font-semibold text-primary">
                      <Sparkles className="w-3.5 h-3.5" /> Stai usando i crediti extra
                    </div>
                    <p className="text-muted-foreground mt-1">
                      Generazioni del piano esaurite. Hai <span className="text-white font-semibold">{usage.ai_credits}</span> crediti AI extra disponibili.
                    </p>
                  </div>
                ) : (
                  <div className="mb-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-xs" data-testid="ai-limit-notice">
                    <div className="flex items-center gap-1.5 font-semibold text-destructive">
                      <AlertTriangle className="w-3.5 h-3.5" /> Generazioni AI esaurite
                    </div>
                    <p className="text-muted-foreground mt-1">
                      Hai usato tutte le {usage.ai_limit} generazioni. Puoi continuare a modificare a mano;
                      l'AI tornerà disponibile tra:
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5 font-mono text-sm text-white" data-testid="ai-limit-countdown">
                      <Clock className="w-3.5 h-3.5 text-primary" />
                      {resetLeft > 0
                        ? `${String(Math.floor(resetLeft / 60)).padStart(2, "0")}:${String(resetLeft % 60).padStart(2, "0")}`
                        : "Pronto! Riprova ora"}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      {usage.plan !== "pro" && (
                        <button onClick={() => navigate("/upgrade")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline" data-testid="ai-limit-upgrade-link">
                          <Sparkles className="w-3.5 h-3.5" /> Passa a Pro
                        </button>
                      )}
                      <button onClick={() => navigate("/upgrade")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-white" data-testid="ai-limit-credits-link">
                        <Coins className="w-3.5 h-3.5" /> Acquista crediti
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex items-end gap-2">
                  <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} disabled={streaming} placeholder={html ? "Chiedi una modifica..." : "Descrivi la pagina..."} className="bg-white/5 border-white/10 min-h-[44px] max-h-32 resize-none py-2.5" data-testid="chat-input" />
                  <Button onClick={sendMessage} disabled={streaming || !input.trim()} className="h-11 w-11 shrink-0 bg-primary text-black hover:bg-[#33F3FF] p-0" data-testid="send-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right panel - preview */}
        <div className={`flex-1 min-h-0 bg-[#0d0d0d] ${mobileTab === "preview" ? "flex" : "hidden md:flex"} flex-col`} data-testid="preview-panel">
          <div className="h-12 shrink-0 border-b border-white/10 flex items-center justify-between px-3 gap-2 overflow-x-auto">
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
                <button onClick={() => editMode && switchMode(false)} disabled={!canEdit} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-40 ${!editMode ? "bg-primary text-black font-semibold" : "text-muted-foreground hover:text-white"}`} data-testid="mode-preview-toggle"><Eye className="w-3.5 h-3.5" /> Anteprima</button>
                <button onClick={() => !editMode && switchMode(true)} disabled={!canEdit} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors disabled:opacity-40 ${editMode ? "bg-primary text-black font-semibold" : "text-muted-foreground hover:text-white"}`} data-testid="mode-edit-toggle"><MousePointerClick className="w-3.5 h-3.5" /> Modifica</button>
              </div>
              {editMode && (
                <>
                  <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
                    <button onClick={() => applyHistory("undo")} disabled={!canUndo} className="p-1.5 rounded-md text-muted-foreground hover:text-white disabled:opacity-30" title="Annulla" data-testid="undo-btn"><Undo2 className="w-4 h-4" /></button>
                    <button onClick={() => applyHistory("redo")} disabled={!canRedo} className="p-1.5 rounded-md text-muted-foreground hover:text-white disabled:opacity-30" title="Ripeti" data-testid="redo-btn"><Redo2 className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => setShowSections((s) => !s)} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${showSections ? "bg-primary text-black font-semibold" : "bg-white/5 text-muted-foreground hover:text-white"}`} data-testid="sections-toggle"><Layers className="w-3.5 h-3.5" /> Sezioni</button>
                  <button onClick={toggleMove} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${moveMode ? "bg-primary text-black font-semibold" : "bg-white/5 text-muted-foreground hover:text-white"}`} data-testid="move-toggle"><Move className="w-3.5 h-3.5" /> Sposta</button>
                </>
              )}
            </div>
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5 shrink-0">
              {Object.entries(DEVICES).map(([key, d]) => (
                <button key={key} onClick={() => setDevice(key)} title={d.label} className={`p-1.5 rounded-md transition-colors ${device === key ? "bg-primary text-black" : "text-muted-foreground hover:text-white"}`} data-testid={`device-${key}`}><d.icon className="w-4 h-4" /></button>
              ))}
            </div>
          </div>

          {publishedUrl && (
            <div className="shrink-0 bg-success/10 border-b border-success/20 px-4 py-2 flex items-center justify-between gap-2 text-xs">
              <span className="text-success truncate font-mono">{publishedUrl}</span>
              <button onClick={copyUrl} className="text-success hover:text-white flex items-center gap-1 shrink-0" data-testid="copy-url-btn">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copiato" : "Copia link"}</button>
            </div>
          )}

          <div className="flex-1 min-h-0 relative">
            <div className={`absolute inset-0 overflow-auto flex justify-center ${device !== "desktop" ? "py-6 px-4 bg-[#0a0a0a]" : ""}`}>
              {iframeSrc ? (
                <div className={`h-full bg-white transition-all duration-300 ${device !== "desktop" ? "rounded-xl overflow-hidden border border-white/10 shadow-2xl" : ""}`} style={{ width: DEVICES[device].w, maxWidth: "100%" }}>
                  <iframe ref={iframeRef} key={editMode ? "edit" : "preview"} title="preview" srcDoc={iframeSrc} className="w-full h-full border-0 bg-white" sandbox="allow-scripts allow-popups allow-forms" data-testid="preview-iframe" />
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6"><Monitor className="w-10 h-10 text-white/15 mb-4" /><p className="text-muted-foreground text-sm max-w-xs">Questa pagina è vuota. Usa la chat o un modello per crearla.</p></div>
              )}
            </div>
            {streaming && (<div className="absolute top-3 right-3 bg-black/70 backdrop-blur border border-primary/30 rounded-full px-3 py-1 text-xs text-primary flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Generazione...</div>)}
            {editMode && showSections && (<SectionsPanel sections={sections} onReorder={reorderSections} onDelete={deleteSection} onInsert={insertSection} onClose={() => setShowSections(false)} />)}
            {editMode && (<Inspector info={selected} onCmd={sendCmd} onUpload={uploadImage} onClose={() => { sendCmd("deselect"); setSelected(null); }} onDelete={() => { sendCmd("delete"); setSelected(null); }} />)}
          </div>
        </div>
      </div>

      <TemplateGallery open={showGallery} onClose={() => setShowGallery(false)} onPick={pickTemplate} />

      <Dialog open={!!pageDialog} onOpenChange={(o) => !o && setPageDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-sm" data-testid="page-dialog">
          <DialogHeader><DialogTitle className="font-display">{pageDialog?.mode === "add" ? "Nuova pagina" : "Rinomina pagina"}</DialogTitle></DialogHeader>
          <Input autoFocus value={pageDialog?.label || ""} onChange={(e) => setPageDialog((d) => ({ ...d, label: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") submitPageDialog(); }} placeholder="Es. Portfolio, Blog, Prezzi..." className="bg-white/5 border-white/10 h-11" data-testid="page-dialog-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPageDialog(null)} className="bg-transparent border-white/10 hover:bg-white/5">Annulla</Button>
            <Button onClick={submitPageDialog} className="bg-primary text-black hover:bg-[#33F3FF] font-semibold" data-testid="page-dialog-submit">{pageDialog?.mode === "add" ? "Crea" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={publishDialog} onOpenChange={(o) => !o && setPublishDialog(false)}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-md" data-testid="publish-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Rocket className="w-4 h-4 text-primary" /> Pubblica il sito</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-muted-foreground">Indirizzo del sito</label>
              <div className="mt-1.5 flex items-center rounded-lg border border-white/10 bg-white/5 overflow-hidden focus-within:border-white/20">
                <span className="pl-3 pr-1 text-xs text-muted-foreground font-mono shrink-0 hidden sm:inline">.../api/s/</span>
                <Input
                  autoFocus
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && slugStatus?.available) doPublish(); }}
                  placeholder="la-mia-pizzeria"
                  className="border-0 bg-transparent focus-visible:ring-0 font-mono h-11 px-2"
                  data-testid="publish-slug-input"
                />
              </div>
              <div className="mt-1.5 text-xs font-mono text-muted-foreground break-all" data-testid="publish-url-preview">
                {process.env.REACT_APP_BACKEND_URL}/api/s/<span className="text-primary">{clientSlugify(slugInput) || "..."}</span>
              </div>
            </div>
            {slugInput && (
              <div className="text-xs flex items-center gap-1.5" data-testid="publish-slug-status">
                {slugStatus?.checking ? (
                  <span className="text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Controllo disponibilità...</span>
                ) : !clientSlugify(slugInput) || slugStatus?.valid === false ? (
                  <span className="text-destructive flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Indirizzo non valido</span>
                ) : slugStatus?.available === false ? (
                  <span className="text-destructive flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Indirizzo già in uso, scegline un altro</span>
                ) : slugStatus?.available ? (
                  <span className="text-success flex items-center gap-1.5"><Check className="w-3 h-3" /> Disponibile</span>
                ) : null}
              </div>
            )}
            {user?.plan === "pro" ? (
              <div className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="showcase-toggle-row">
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Mostra in vetrina pubblica</div>
                  <p className="text-xs text-muted-foreground mt-0.5">Il tuo sito potrà apparire nella galleria pubblica di Angiò A.I..</p>
                </div>
                <Switch checked={showcaseOn} onCheckedChange={setShowcaseOn} data-testid="showcase-switch" />
              </div>
            ) : (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="showcase-pro-hint">
                <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
                <span>Con <button onClick={() => navigate("/upgrade")} className="text-primary font-semibold hover:underline">Angiò A.I. Pro</button> il tuo sito può entrare nella vetrina pubblica.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialog(false)} className="bg-transparent border-white/10 hover:bg-white/5">Annulla</Button>
            <Button onClick={doPublish} disabled={publishing || slugStatus?.available === false || slugStatus?.checking || !clientSlugify(slugInput)}
              className="bg-primary text-black hover:bg-[#33F3FF] font-semibold disabled:opacity-40" data-testid="publish-confirm-btn">
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1.5" /> Pubblica ora</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
